import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getGroupedCollectionsAsync } from '../utils/inventoryService';
import LazyImage from './LazyImage';
import useDeviceDetection from '../hooks/useDeviceDetection';

// Hero image is served from public folder
const heroImage = '/images/Hero.jpg';

// Helper function to get the correct thumbnail path based on collection
const getThumbnailPath = (collection) => {
  // All collections now use standard 01.jpg naming
  return `/images/collections/${collection.folder}/01.jpg`;
};

const HomeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 20px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureImageContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FeatureImage = styled.div`
  width: 100%;
  height: auto;
  max-height: 80vh;
  border-radius: 2px;

  @media (max-width: 768px) {
    max-height: 50vh;
    height: 50vh;
  }

  @media (max-width: 480px) {
    max-height: 40vh;
    height: 40vh;
  }
`;

const CollectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CollectionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  position: sticky;
  top: 180px;
  background-color: #000;
  z-index: 100;
  padding: 15px 0;
  margin-top: -15px;

  @media (max-width: 768px) {
    top: 140px;
  }
`;

const CollectionsTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 1px;
`;

const CollectionGroup = styled.div`
  display: flex;
  flex-direction: column;
  opacity: ${props => (props.active ? 1 : 0)};
  position: ${props => (props.active ? 'relative' : 'absolute')};
  transition: opacity 0.8s ease-in-out;
  width: 100%;
  top: 0;
  left: 0;
  pointer-events: ${props => (props.active ? 'auto' : 'none')};
`;

const CollectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 15px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const CollectionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CollectionTitle = styled.h3`
  font-size: 0.8rem;
  font-weight: 400;
  text-align: center;
  color: #ccc;
`;

const CollectionImageLink = styled(Link)`
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  transition: transform 0.3s ease-in-out;
  position: relative;
  display: block;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    aspect-ratio: 4/3;
    max-height: 250px;

    &:hover {
      transform: none; /* Disable hover on mobile */
    }
  }

  @media (max-width: 480px) {
    aspect-ratio: 3/2;
    max-height: 200px;
  }
`;

const CollectionImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PriceTag = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 3px;
  font-weight: bold;
  font-size: 0.85rem;
`;

const StatusTag = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(150, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 3px;
  font-weight: bold;
  font-size: 0.75rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: #ccc;
  font-size: 1.2rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: #ff6b6b;
  font-size: 1rem;
  gap: 15px;
`;

const RetryButton = styled.button`
  background: #333;
  color: #fff;
  padding: 10px 20px;
  border-radius: 3px;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: #555;
  }
`;

function Home() {
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [groupedCollections, setGroupedCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const { isMobile, isTablet } = useDeviceDetection();

  // Load collections data from server
  useEffect(() => {
    let mounted = true;

    const loadCollections = async () => {
      try {
        setLoading(true);
        setError(null);

        const collections = await getGroupedCollectionsAsync();

        if (mounted) {
          setGroupedCollections(collections);
          // Native browser lazy loading and LazyImage component handle image optimization
        }
      } catch (err) {
        console.error('Error loading collections:', err);
        if (mounted) {
          setError('Failed to load collections. Please try again.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadCollections();

    return () => {
      mounted = false;
    };
  }, [activeGroupIndex]);

  // Check for group parameter in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const groupParam = params.get('group');
    if (groupParam !== null && groupedCollections.length > 0) {
      const groupIndex = parseInt(groupParam, 10);
      if (!isNaN(groupIndex) && groupIndex >= 0 && groupIndex < groupedCollections.length) {
        setActiveGroupIndex(groupIndex);
      }
    }
  }, [location.search, groupedCollections]);

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <HomeContainer>
      <FeatureImageContainer>
        <FeatureImage>
          <LazyImage
            src={heroImage}
            alt="Joey M. Robinson artwork"
            objectFit="contain"
            hoverEffect={false}
          />
        </FeatureImage>
      </FeatureImageContainer>

      <CollectionsContainer>
        <CollectionsHeader>
          <CollectionsTitle>COLLECTIONS</CollectionsTitle>
        </CollectionsHeader>

        {loading ? (
          <LoadingContainer>Loading collections...</LoadingContainer>
        ) : error ? (
          <ErrorContainer>
            <div>{error}</div>
            <RetryButton onClick={handleRetry}>Retry</RetryButton>
          </ErrorContainer>
        ) : (
          <div style={{ position: 'relative' }}>
            {groupedCollections.map((group, index) => (
              <CollectionGroup key={index} active={index === activeGroupIndex}>
                <CollectionsGrid>
                  {group.map((collection) => (
                    <CollectionItem key={collection.id}>
                      <CollectionTitle>{collection.title}</CollectionTitle>
                      <CollectionImageLink to={`/collection/${collection.id}?from=${index}`}>
                        <LazyImage
                          src={getThumbnailPath(collection)}
                          alt={collection.title}
                          hoverEffect={!isMobile}
                        />
                      </CollectionImageLink>
                    </CollectionItem>
                  ))}
                </CollectionsGrid>
              </CollectionGroup>
            ))}
          </div>
        )}
      </CollectionsContainer>
    </HomeContainer>
  );
}

export default Home;